import { AlertTriangle, Archive, Info, X } from 'lucide-react'
import type { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import type { ThreadStatus } from '@/features/agent/ThreadStatusContext'

/** Format an ISO timestamp as a short local time, tolerating bad input. */
function formatTime(iso?: string): string | null {
  if (!iso) return null
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? null : d.toLocaleString()
}

export interface ThreadStatusBannerViewProps {
  status: ThreadStatus | null
  onDismiss: () => void
  /** When provided and the thread is full, renders the "Start new thread" CTA. */
  onStartNewThread?: () => void
}

/**
 * Pure notice shown above the composer when the backend trimmed the thread or
 * refused the request because it's still over budget. The connected
 * {@link ThreadStatusBanner} feeds it from `ThreadStatusContext`.
 */
export const ThreadStatusBannerView: FC<ThreadStatusBannerViewProps> = ({
  status,
  onDismiss,
  onStartNewThread,
}) => {
  const { t } = useTranslation()

  if (!status) return null

  const isFull = status.kind === 'full'
  const isCompacted = status.kind === 'compacted'
  const Icon = isFull ? AlertTriangle : isCompacted ? Archive : Info
  const tone = isFull
    ? 'border-destructive/40 bg-destructive/10 text-destructive'
    : isCompacted
      ? 'border-border bg-muted text-muted-foreground'
      : 'border-warning-border bg-warning-surface text-warning-foreground'

  const compactedAt = formatTime(status.compacted_at)

  let title: string
  let body: string
  if (isFull) {
    title = t('chat.thread.full.title', 'This conversation is too long to continue')
    body = t(
      'chat.thread.full.body',
      'The thread exceeds the model context window even after trimming. Start a new conversation to keep going.'
    )
  } else if (isCompacted) {
    title = compactedAt
      ? t('chat.thread.compacted.titleAt', 'Context compacted at {{time}}', { time: compactedAt })
      : t('chat.thread.compacted.title', 'Context compacted')
    body = t(
      'chat.thread.compacted.body',
      'The assistant summarized earlier messages to free up space and keep responding. Your full conversation is preserved here — only what the model keeps in mind was condensed.'
    )
  } else {
    title = t('chat.thread.trimmed.title', 'Earlier context was condensed')
    body = t(
      'chat.thread.trimmed.body',
      'Older tool results were summarized to fit. For best answers on long sessions, consider starting a new thread.'
    )
  }

  return (
    <div
      className={`flex items-start gap-2 rounded-md border px-3 py-2 text-xs ${tone}`}
      role={isFull ? 'alert' : 'status'}
    >
      <Icon className="mt-0.5 size-3.5 shrink-0" />
      <div className="flex-1">
        <div className="font-medium">{title}</div>
        <div className="mt-0.5 text-[11px] opacity-80">{body}</div>
        {isFull && onStartNewThread && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="mt-2 h-7 text-[11px] max-md:h-9 max-md:text-xs"
            onClick={() => onStartNewThread()}
          >
            {t('chat.thread.full.cta', 'Start new thread')}
          </Button>
        )}
      </div>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="size-9 shrink-0 opacity-60 hover:opacity-100 md:size-6"
        onClick={onDismiss}
        aria-label={t('common.dismiss', 'Dismiss')}
      >
        <X className="size-3.5" />
      </Button>
    </div>
  )
}
